import { useGetOrder } from 'ClientPortal/src/api/Queries/orders'
import { useGetAppraiserUsers, useGetCustomerUsers } from 'ClientPortal/src/api/Queries/users'
import { useDownloadReport, useGetValuation, useGetValuations } from 'ClientPortal/src/api/Queries/valuations'
import useTabPath from 'ClientPortal/src/router/useTabPath'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { useGetAsset } from 'ClientPortal/src/api/Queries/assets'
import { sortBy } from 'lodash'
import dayjs from 'dayjs'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useTranslation } from 'react-i18next'

const getDraftChipLabel = (title: string | undefined, draftLabel: string) => {
    const draftVersion = title?.split(' - ')[0]?.trim().match(/\d+/)?.[0]
    return draftVersion ? `${draftLabel} ${draftVersion}` : draftLabel
}

export function useValuationPageLayout() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { assetId, valuationId } = useTypedParams(['assetId', 'valuationId'])
    const selectedTab = useTabPath(['overview', 'income', 'dcf', 'market-data', 'tenant-list'])
    const { valuation, isValuationPending } = useGetValuation({ valuationId, throwOnError: throwOnForbiddenApiError })
    const { valuations: allValuations, isValuationsPending } = useGetValuations({
        assetId,
        includeDrafts: true,
        filterFinalized: false,
    })
    const valuations = allValuations.filter((valuationItem) => valuationItem.status === 'finalised' || valuationItem.is_draft)
    const { asset, isAssetPending } = useGetAsset({ assetId, throwOnError: throwOnForbiddenApiError })
    const { downloadReport, isDownloadingReport } = useDownloadReport(assetId)
    const { isComparablesEnabled } = useFlags()

    const { order, isOrderInitialLoading } = useGetOrder({ orderId: valuation?.order_uuid })
    const { customerUsers, isCustomerUsersPending } = useGetCustomerUsers({
        orderId: valuation?.order_uuid,
        enabled: !!valuation?.order_uuid,
    })
    const { appraiserUsers, isAppraiserUsersPending } = useGetAppraiserUsers({
        orderId: valuation?.order_uuid,
        enabled: !!valuation?.order_uuid,
    })
    const assignedCustomerUsers =
        order?.assigned_customer_users
            .map((customerUserId) => customerUsers.find((customerUser) => customerUser.id === customerUserId))
            .filter((customerUser) => customerUser !== undefined) ?? []

    const valuationsWithSameOrder = valuations
        .filter((valuationItem) => valuationItem.order_uuid === valuation?.order_uuid && valuationItem.valuation_type !== 'historical')
        .map((valuationItem) => ({
            valuationId: valuationItem.uuid,
            valuationVersion: valuationItem.is_draft ? getDraftChipLabel(valuationItem.title, tCommon('draft')) : tCommon('final'),
            date: valuationItem.is_draft ? valuationItem.created_on : valuationItem.valuation_date,
        }))
    const valuationOptions = sortBy(valuationsWithSameOrder, (option) => dayjs(option.date).valueOf()).toReversed()
    const parentValuation = valuation?.is_draft
        ? allValuations.find((valuationItem) => !valuationItem.is_draft && valuationItem.drafts.includes(valuation.uuid))
        : valuation

    return {
        valuation,
        valuationStatus: parentValuation?.status,
        showPlausibility: !!order && !order.external_participant_mailbox,
        asset,
        assetId,
        assignedCustomerUsers,
        order,
        isOrderInitialLoading,
        isCustomerUsersPending,
        isValuationPageLayoutPending: isValuationPending || isAssetPending,
        appraiserUsers,
        isAppraiserUsersPending,
        selectedTab,
        downloadReport,
        isDownloadingReport,
        valuationOptions,
        isValuationsPending,
        isComparablesEnabled,
    }
}
